import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from '../../../utils/permissionUtils';
import { useLocation } from "react-router-dom";
import DataTable from 'react-data-table-component';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Drawer,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  accent: '#f59e0b',
};

const ExpandedComponent = ({ data }) => {
  return (
    <Box sx={{
      p: 3,
      bgcolor: 'rgba(245,158,11,0.05)',
      borderLeft: '3px solid #f59e0b',
      m: 1,
      borderRadius: '0 12px 12px 0',
    }}>
      <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
        Exception Details - {data.error_code}
      </Typography>
      <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
        <strong>Message:</strong> {data.message}
      </Typography>
    </Box>
  );
};

export default function ListExceptionLog() {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fetchedUrl = queryParams.get('fetched_url');

  const [exceptionLogs, setExceptionLogs] = useState([]);
  const [filteredExceptionLogs, setFilteredExceptionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reload, setReload] = useState(false);

  const [canView, setCanView] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('chat-history', 'view');
        setCanView(viewPerm);
      } catch (error) {
        setCanView(false);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  const handleReload = () => setReload(!reload);

  useEffect(() => {
    document.title = "Exception Logs | KlonIT Web Application";
    if (canView && !checkingPermissions) {
      fetchExceptionLogs();
    }
  }, [reload, canView, checkingPermissions]);

  const fetchExceptionLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/get-exception-log`);
      const responseData = typeof response.data.data === 'string'
        ? JSON.parse(response.data.data)
        : response.data.data;
      setExceptionLogs(responseData);
      setFilteredExceptionLogs(responseData);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = exceptionLogs.filter(
      (doc) =>
        (doc.error_code?.toString() ?? '').includes(value) ||
        doc.log_type.toLowerCase().includes(value) ||
        doc.status.toLowerCase().includes(value) ||
        doc.created_on.toLowerCase().includes(value) ||
        doc.updated_on.toLowerCase().includes(value)
    );
    setFilteredExceptionLogs(filtered);
  };

  const columns = [
    { name: "Reference ID", selector: (row) => row.error_code, sortable: true },
    { name: "Log Type", selector: (row) => row.log_type, sortable: true },
    { name: "Status", selector: (row) => row.status, sortable: true },
    { name: "Created On", selector: (row) => row.created_on, sortable: true },
    { name: "Updated On", selector: (row) => row.updated_on, sortable: true }
  ];

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: 'transparent',
        borderRadius: '12px',
        overflow: 'hidden',
      },
    },
    headRow: {
      style: {
        backgroundColor: 'rgba(245,158,11,0.08)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        minHeight: '48px',
      },
    },
    headCells: {
      style: {
        fontFamily,
        fontWeight: 600,
        fontSize: '0.8rem',
        color: colors.textPrimary,
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        fontFamily,
        fontSize: '0.85rem',
        color: colors.textSecondary,
        backgroundColor: 'transparent',
        minHeight: '52px',
        borderBottom: '1px solid rgba(0,0,0,0.04)',
        '&:hover': {
          backgroundColor: 'rgba(245,158,11,0.04)',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    pagination: {
      style: {
        fontFamily,
        backgroundColor: 'transparent',
        borderTop: '1px solid rgba(0,0,0,0.06)',
        color: colors.textSecondary,
      },
    },
  };

  const toggleDrawer = (open) => setIsDrawerOpen(open);

  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={30} sx={{ color: colors.accent }} />
        <Typography sx={{ fontFamily, color: colors.textSecondary }}>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ maxWidth: '600px', mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ fontFamily, borderRadius: '12px' }}>
          <strong>Access Denied</strong><br />
          You don't have permission to view logs. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily, maxWidth: '1100px', width: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          boxShadow: '0 8px 24px rgba(245,158,11,0.3)',
        }}>
          <BugReportIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em' }}>
          Exception Logs
        </Typography>
        <Typography sx={{ fontFamily, fontSize: '0.875rem', color: colors.textTertiary, mt: 0.5 }}>
          Monitor and track system exceptions and errors
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{
        bgcolor: 'rgba(255,255,255,0.8)',
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        p: 3,
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        {/* Search Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>
            All Logs ({filteredExceptionLogs.length})
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textTertiary, fontSize: '1.1rem' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: '280px',
              '& .MuiOutlinedInput-root': {
                fontFamily,
                fontSize: '0.875rem',
                borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.8)',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
                '&:hover fieldset': { borderColor: colors.accent },
                '&.Mui-focused fieldset': { borderColor: colors.accent },
              },
            }}
          />
        </Box>

        {/* Data Table */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={36} sx={{ color: colors.accent }} />
            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: colors.textSecondary, mt: 2 }}>
              Loading exception logs...
            </Typography>
          </Box>
        ) : filteredExceptionLogs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 2 }} />
            <Typography sx={{ fontFamily, fontSize: '1rem', color: colors.textSecondary }}>
              No exception logs available
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textTertiary, mt: 0.5 }}>
              Logs will appear here when system exceptions occur
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            data={filteredExceptionLogs}
            pagination
            responsive
            highlightOnHover
            pointerOnHover
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            customStyles={customDataTableStyles}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 3, fontFamily, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => toggleDrawer(false)}
        PaperProps={{ sx: { width: "500px", borderRadius: '16px 0 0 16px' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Drawer>
    </Box>
  );
}
